import { useEffect, useState, type FormEvent } from "react";
import { ApiError, fetchGuestConfig, requestCode, verifyCode } from "./api.js";
import styles from "./Live.module.css";
import { go } from "./nav.js";
import { Turnstile } from "./Turnstile.js";

/**
 * Sign-in for client participants (brief 8.2): their email address, a Turnstile check, then the
 * six-digit code emailed to them. Codes rather than links, because email scanners open links.
 */
export function GuestSignIn({ mapId }: { mapId?: string }) {
  const [siteKey, setSiteKey] = useState<string | null | undefined>(undefined);
  const [step, setStep] = useState<"email" | "code">("email");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [token, setToken] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // A new widget for each code request: a Turnstile token can be used once.
  const [widget, setWidget] = useState(0);

  useEffect(() => {
    const ctl = new AbortController();
    fetchGuestConfig(ctl.signal).then(
      (c) => setSiteKey(c.turnstileSiteKey),
      () => setSiteKey(null),
    );
    return () => ctl.abort();
  }, []);

  const staffReturn = mapId ? `?returnTo=${encodeURIComponent(`/maps/${mapId}`)}` : "";

  async function sendCode(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await requestCode({ email, mapId, turnstileToken: token ?? undefined });
      setStep("code");
      setCode("");
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "The code could not be sent. Try again.");
    } finally {
      setBusy(false);
      setToken(null);
      setWidget((w) => w + 1);
    }
  }

  async function signIn(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await verifyCode({ email, code: code.trim() });
      go(mapId ? `/maps/${encodeURIComponent(mapId)}` : "/");
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Sign-in did not complete. Try again.");
      setBusy(false);
    }
  }

  const needsCheck = Boolean(siteKey);

  return (
    <main className={styles.guestWrap}>
      <div className={styles.card}>
        <div className={styles.eyebrow}>SalesFix Value Map Builder</div>

        {step === "email" && (
          <form onSubmit={(e) => void sendCode(e)} noValidate>
            <h1>Sign in to your value map</h1>
            <p className={styles.note}>
              Enter the email address your invitation was sent to. We will email you a six-digit
              sign-in code.
            </p>
            <div className={styles.field}>
              <label htmlFor="guest-email">Email address</label>
              <input
                id="guest-email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            {siteKey && <Turnstile key={widget} siteKey={siteKey} onToken={setToken} />}
            {error && (
              <p className={styles.error} role="alert">
                {error}
              </p>
            )}
            <div className={styles.actions}>
              <button
                type="submit"
                className={`${styles.smallBtn} ${styles.primaryBtn}`}
                disabled={busy || !email.trim() || siteKey === undefined || (needsCheck && !token)}
              >
                Send code
              </button>
            </div>
          </form>
        )}

        {step === "code" && (
          <form onSubmit={(e) => void signIn(e)} noValidate>
            <h1>Check your email</h1>
            <p className={styles.note} role="status">
              If {email} has been invited, a six-digit code is on its way. It is valid for 10
              minutes.
            </p>
            <div className={styles.field}>
              <label htmlFor="guest-code">Sign-in code</label>
              <input
                id="guest-code"
                className={styles.code}
                inputMode="numeric"
                autoComplete="one-time-code"
                pattern="[0-9]{6}"
                maxLength={6}
                required
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
              />
            </div>
            {error && (
              <p className={styles.error} role="alert">
                {error}
              </p>
            )}
            <div className={styles.actions}>
              <button
                type="submit"
                className={`${styles.smallBtn} ${styles.primaryBtn}`}
                disabled={busy || code.length !== 6}
              >
                Sign in
              </button>
              <button
                type="button"
                className={styles.linkBtn}
                onClick={() => {
                  setStep("email");
                  setError(null);
                }}
              >
                Use a different email or send a new code
              </button>
            </div>
          </form>
        )}

        <p className={styles.hint}>
          SalesFix staff:{" "}
          <a href={`/auth/salesforce/login${staffReturn}`}>sign in with Salesforce</a>.
        </p>
      </div>
    </main>
  );
}
